/**
 * Thrive Student Living — Main JS Entry
 * Swiper CSS is loaded via wp_enqueue_style in enqueue.php
 */

import { Navigation } from './modules/navigation.js';
import { initAllSwipers } from './modules/carousel.js';
import { Tabs } from './modules/tabs.js';
import { SearchBar } from './modules/search.js';
import { ScrollAnimations } from './modules/animations.js';
import { UgcSlider } from './modules/ugc-slider.js';
import { InitiativesSlider } from './modules/initiatives-slider.js';
import { TestimonialsCarousel } from './modules/testimonials.js';
import { StickySearch } from './modules/sticky-search.js';
import { HeroParallax } from './modules/parallax.js';
import { ResidencesBlock } from '../../blocks/residences/residences.js';
import { PartnershipsCarousel } from '../../blocks/partnerships/partnerships.js';
import { ContactTabs } from './modules/contact-tabs.js';
import { Cf7Dropdown } from './modules/cf7-dropdown.js';
import { Cf7Toast } from './modules/cf7-toast.js';
import { HubspotFormEnhance } from './modules/hubspot-form-enhance.js';
import { ResidencePage } from './modules/residence-page.js';
import { LocationPage } from './modules/location-page.js';
import { SearchResults } from './modules/search-results.js';
import { FaqsBlock } from '../../blocks/faqs/faqs.js';
import { ResidentLifeVideo } from '../../blocks/resident-life-section/resident-life-section.js';
import { HelpPolicies } from '../../blocks/help-policies/help-policies.js';
import { ApplyLoader } from './modules/apply-loader.js';
import { initCareerOpenings } from './modules/career-openings.js';

function initThriveModules() {
	Navigation.init();
	initAllSwipers();
	Tabs.initAll();
	SearchBar.init();
	ScrollAnimations.init();
	UgcSlider.init();
	InitiativesSlider.init();
	TestimonialsCarousel.init();
	StickySearch.init();
	HeroParallax.init();
	ResidencesBlock.init();
	PartnershipsCarousel.init();
	ContactTabs.init();
	Cf7Dropdown.init();
	Cf7Toast.init();
	HubspotFormEnhance.init();
	ResidencePage.init();
	LocationPage.init();
	SearchResults.init();
	FaqsBlock.init();
	ResidentLifeVideo.init();
	HelpPolicies.init();
	ApplyLoader.init();
	initCareerOpenings();

	// Re-observe after parallax wraps content (DOM changed)
	ScrollAnimations.observe();
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initThriveModules );
} else {
	initThriveModules();
}

// ─── Gutenberg editor preview support ───
// Inside the block editor, ACF blocks are re-rendered on every data change
// (removing and re-injecting their markup). Watch for those mutations and
// re-initialize Swipers / tabs / carousels so previews look like the site.
if ( document.body && document.body.classList.contains( 'block-editor-page' ) === false ) {
	// Runs inside iframe canvas — body class is set by Gutenberg.
}

( function watchEditorPreviews() {
	// Heuristic: we're in an editor context if any of these exist.
	const isEditor =
		document.body && (
			document.body.classList.contains( 'wp-admin' ) ||
			document.querySelector( '.block-editor' ) ||
			document.querySelector( '.editor-styles-wrapper' ) ||
			window.location.pathname.includes( '/wp-admin/' )
		);
	if ( ! isEditor ) return;

	let reinitTimer = null;
	const scheduleReinit = () => {
		clearTimeout( reinitTimer );
		reinitTimer = setTimeout( () => {
			try {
				initAllSwipers();
				ResidencesBlock.init();
				PartnershipsCarousel.init();
				Tabs.initAll();
				UgcSlider.init();
				InitiativesSlider.init();
				TestimonialsCarousel.init();
				initCareerOpenings();
				FaqsBlock.init();
				ResidentLifeVideo.init();
				HelpPolicies.init();
				// ResidencePage handles amenity/about/room-gallery Swipers used
				// inside residence single-page blocks. Must re-run on block mount
				// since ACF replaces the block DOM wholesale on each data change.
				ResidencePage.init();
			} catch ( e ) {
				// Silent — editor DOM can be in weird states during mounts.
			}
		}, 120 );
	};

	const observer = new MutationObserver( ( mutations ) => {
		for ( const m of mutations ) {
			for ( const node of m.addedNodes ) {
				if ( node.nodeType !== 1 ) continue;
				if (
					node.matches?.(
						'.block-residences, .block-partnerships, .block-faqs, .block-resident-life-section, .block-testimonials, .amenities-section, .room-section, [class*="block-"]'
					) ||
					node.querySelector?.(
						'.block-residences, .block-partnerships, .block-faqs, .block-resident-life-section, .block-testimonials, .amenities-section, .room-section, [data-testimonials-carousel], [data-amenity-swiper], .swiper'
					)
				) {
					scheduleReinit();
					return;
				}
			}
		}
	} );

	observer.observe( document.body, { childList: true, subtree: true } );
	// Initial pass in case blocks are already mounted.
	scheduleReinit();
} )();
